import { addMonths, format, isValid, parse } from 'date-fns';

export const maskDateInput = (value: string): string => {
  // PHA-1285: an edit inside an already-formatted date (select "04", type "1") must stay in
  // its segment. Re-flowing the digits turned "1/26/2027" into "12/62/027".
  if (/^\d{1,2}\/\d{1,2}\/\d{1,4}$/.test(value)) return value;
  const digits = value.replace(/\D/g, '');
  const truncated = digits.slice(0, 8);

  let result = truncated;
  if (result.length > 2) {
    result = result.slice(0, 2) + '/' + result.slice(2);
  }
  if (result.length > 5) {
    result = result.slice(0, 5) + '/' + result.slice(5);
  }

  return result;
};

/** ISO yyyy-MM-dd -> masked MM/DD/YYYY for display/editing. '' if empty/invalid. */
export const isoToMaskedDate = (iso?: string | null): string => {
  if (!iso) return '';
  // accept yyyy-MM-dd (avoid TZ shift) or a fuller ISO timestamp
  const datePart = iso.slice(0, 10);
  const d = parse(datePart, 'yyyy-MM-dd', new Date());
  return isValid(d) ? format(d, 'MM/dd/yyyy') : '';
};

/** masked MM/DD/YYYY -> ISO yyyy-MM-dd. null if not a complete valid calendar date. */
export const maskedDateToIso = (masked: string): string | null => {
  if (!masked || masked.length !== 10) return null;
  const d = parse(masked, 'MM/dd/yyyy', new Date());
  return isValid(d) ? format(d, 'yyyy-MM-dd') : null;
};

/** true if masked is a complete valid date strictly in the future (after today). */
export const isFutureMaskedDate = (masked: string): boolean => {
  const iso = maskedDateToIso(masked);
  if (!iso) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = parse(iso, 'yyyy-MM-dd', new Date());
  return d.getTime() > today.getTime();
};

/**
 * PHA-1080: the care plan review cycle to use when the clinic's setting has not
 * been resolved (or the org has none configured). Six months is the PCMH
 * recommendation and is what this file hardcoded before the setting existed, so
 * every unconfigured clinic keeps today's behaviour. Mirrors
 * DEFAULT_CARE_PLAN_REVIEW_MONTHS in app_logic.py and ccm/app.py.
 */
export const DEFAULT_CARE_PLAN_REVIEW_MONTHS = 6;

/**
 * default review date = today + the clinic's review cycle, masked MM/DD/YYYY.
 *
 * PHA-1080: `months` is REQUIRED on purpose, with no default. This is the value
 * that actually reaches the database on a wizard approval — the approve endpoint
 * computes its own fallback, and then updateCarePlanExpiration immediately
 * overwrites it with whatever this produced. A default parameter here would let a
 * caller silently keep six months while the clinic is configured for twelve, and
 * tsc would not say a word. Callers that genuinely have no org context pass
 * DEFAULT_CARE_PLAN_REVIEW_MONTHS explicitly, so the choice is visible at the
 * call site.
 */
export const defaultExpirationMasked = (months: number): string =>
  format(addMonths(new Date(), months), 'MM/dd/yyyy');
